/**
 * COMPONENTE PRINCIPAL DE LA APLICACIÓN - APP.JS
 * ===============================================
 * 
 * Este es el componente raíz de toda la aplicación React.
 * Aquí se configura el enrutamiento (navegación entre páginas) usando React Router.
 * 
 * RESPONSABILIDADES:
 * 1. Importar todos los estilos globales (App.css)
 * 2. Configurar el BrowserRouter para permitir navegación
 * 3. Definir todas las rutas de la aplicación
 * 4. Asociar cada ruta con su componente de página correspondiente
 * 
 * ESTRUCTURA:
 * - BrowserRouter: Contenedor que habilita el enrutamiento
 * - Routes: Contenedor de todas las rutas
 * - Route: Cada ruta individual con su path y elemento
 */

// Importar estilos globales y animaciones personalizadas
import "./App.css";

// Importar componentes de React Router para navegación
import { BrowserRouter, Routes, Route } from "react-router-dom";

// ============================================
// IMPORTAR TODAS LAS PÁGINAS DE LA APLICACIÓN
// ============================================

// Página principal (home)
import ModernHomePage from "./pages/ModernHomePage";

// Páginas de contenido público
import PreciosPage from "./pages/PreciosPage";
import NoticiasPage from "./pages/NoticiasPage";
import NoticiaDetallePage from "./pages/NoticiaDetallePage";
import ContactoPage from "./pages/ContactoPage";

// Página de administración
import AdminLoginPage from "./pages/AdminLoginPage";
import LoginPage from "./pages/LoginPage";

// Página oculta de documentación técnica
import DocumentacionTecnica from "./pages/DocumentacionTecnica";

// Página de lotes
import LotesPage from "./pages/LotesPage";
import LoteDetallePage from "./pages/LoteDetallePage";

// Página de calendario
import CalendarioPage from "./pages/CalendarioPage";

// Página de remates anteriores
import RematesAnterioresPage from "./pages/RematesAnterioresPage";

// Página de contacto completa
import ContactoCompletaPage from "./pages/ContactoCompletaPage";

/**
 * COMPONENTE APP
 * ==============
 * Componente principal que renderiza toda la aplicación
 * y maneja el sistema de rutas.
 */
function App() {
  return (
    <div className="App">
      {/* 
        BrowserRouter: Proveedor de contexto de enrutamiento
        Permite que todos los componentes hijos usen navegación
      */}
      <BrowserRouter basename="/web">
        {/* 
          Routes: Contenedor que evalúa qué ruta coincide
          con la URL actual y renderiza el componente correspondiente
        */}
        <Routes>
          {/* 
            RUTA PRINCIPAL (HOME)
            ====================
            Path: /
            Descripción: Página de inicio con hero, sponsors, precios, noticias
            Componente: ModernHomePage
          */}
          <Route path="/" element={<ModernHomePage />} />

          {/* 
            RUTA DE REMATES
            ===============
            Path: /remates
            Descripción: Información sobre remates anteriores
            Componente: RematesAnterioresPage
          */}
          <Route path="/remates" element={<RematesAnterioresPage />} />

          {/* 
            RUTA DE PRECIOS
            ===============
            Path: /precios
            Descripción: Monitor de cotizaciones y análisis de mercado
            Estado: En construcción - muestra mensaje de placeholder
            Componente: PreciosPage
          */}
          <Route path="/precios" element={<PreciosPage />} />

          {/* 
            RUTA DE NOTICIAS
            ================
            Path: /noticias
            Descripción: Blog, noticias y novedades del sector ganadero
            Componente: NoticiasPage
          */}
          <Route path="/noticias" element={<NoticiasPage />} />

          {/* 
            RUTA DE DETALLE DE NOTICIA
            ==========================
            Path: /noticias/:slug
            Descripción: Vista completa de una noticia con contenido HTML
            Componente: NoticiaDetallePage
          */}
          <Route path="/noticias/:slug" element={<NoticiaDetallePage />} />

          {/* 
            RUTA DE CONTACTO (PLACEHOLDER ANTIGUO)
            ================
            Path: /contacto
            Descripción: Formulario de contacto y datos de la empresa
            Estado: En construcción - muestra información de contacto básica
            Componente: ContactoPage
          */}
          <Route path="/contacto-old" element={<ContactoPage />} />

          {/* 
            RUTA DE CONTACTO COMPLETA (NUEVA)
            ==================================
            Path: /contacto
            Descripción: Página completa de contacto con mapa, formulario, redes sociales
            Características: Formulario funcional, mapa de Google, departamentos, redes sociales
            Componente: ContactoCompletaPage
          */}
          <Route path="/contacto" element={<ContactoCompletaPage />} />

          {/* 
            RUTA DE LOGIN ADMINISTRATIVO
            ============================
            Path: /admin/login
            Descripción: Página de acceso privado para administradores
            Acceso: Solo administradores con credenciales
            Componente: AdminLoginPage
            
            NOTA: Esta ruta está accesible desde el botón "Admin" (con icono 
            de candado) en el header principal.
          */}
          <Route path="/admin/login" element={<AdminLoginPage />} />

          {/* 
            RUTA DE LOGIN CLIENTES
            ======================
            Path: /login
            Descripción: Página de acceso para clientes
            Componente: LoginPage
          */}
          <Route path="/login" element={<LoginPage />} />

          {/* 
            RUTA DE CALENDARIO
            ==================
            Path: /calendario
            Descripción: Calendario anual de remates con vista por mes y año
            Características: Vista mensual/anual, próximos eventos, descarga
            Componente: CalendarioPage
          */}
          <Route path="/calendario" element={<CalendarioPage />} />

          {/* 
            RUTA DE LOTES
            =============
            Path: /lotes
            Descripción: Página de visualización y filtrado de lotes del remate
            Características: Filtros laterales reactivos, acordeón de categorías, 
                           tabs de Vacunos/Ovinos
            Componente: LotesPage
          */}
          <Route path="/lotes" element={<LotesPage />} />

          {/* 
            RUTA DE DETALLE DE LOTE
            =======================
            Path: /lotes/:numeroLote
            Descripción: Página de detalle completo de un lote específico
            Características: Información completa, tabs (General, Ubicación, Observaciones),
                           video/imágenes, botones de acción
            Componente: LoteDetallePage
          */}
          <Route path="/lotes/:numeroLote" element={<LoteDetallePage />} />

          {/* 
            RUTA DE DOCUMENTACIÓN TÉCNICA (OCULTA)
            =======================================
            Path: /documentacion-tecnica
            Descripción: Manual técnico exhaustivo de la arquitectura del proyecto
            Acceso: Solo mediante URL directa (no hay enlaces públicos a esta página)
            Propósito: Referencia técnica para desarrolladores
            Componente: DocumentacionTecnica
            
            NOTA IMPORTANTE: Esta ruta NO aparece en ningún menú de navegación.
            Es una página "oculta" que solo se puede acceder escribiendo 
            la URL completa en el navegador.
          */}
          <Route path="/documentacion-tecnica" element={<DocumentacionTecnica />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

// Exportar el componente App para que pueda ser usado en index.js
export default App;